import React from "react";
import BarcodeRenderer from "./BarcodeRenderer";
import QRCodeRenderer from "./QRCodeRenderer";
import {getTypeConfig} from "./labelConfig";

/**
 * Rend le contenu d'UNE étiquette à sa taille physique réelle (mm).
 * Utilisé à l'identique dans l'aperçu et à l'impression → garantit le WYSIWYG.
 *
 * @param {string} type            - "product" | "delivery" | "client"
 * @param {object} item            - objet métier (produit / commande / client)
 * @param {{width:number,height:number}} format - dimensions en mm
 * @param {string[]} selectedFields - ids des champs à afficher
 */
const LabelContent = ({type, item, format, selectedFields}) => {
    const config = getTypeConfig(type);
    const fields = config.fields.filter((f) => selectedFields.includes(f.id));

    // Échelle typographique (mm) relative à la hauteur de l'étiquette, bornée pour rester lisible.
    const base = Math.min(Math.max(format.height * 0.07, 1.8), 3); // mm
    const isSmall = format.width <= 45;
    // Taille des codes proportionnelle à la largeur disponible.
    const barcodeHeight = Math.max(24, format.height * 1.1);
    const qrSize = Math.max(40, Math.min(format.width, format.height) * 2.4);

    const renderField = (field) => {
        const value = field.accessor(item);
        if (value == null || value === "") return null;

        switch (field.kind) {
            case "title":
                return (
                    <div key={field.id} style={{fontWeight: 700, fontSize: `${base * 1.15}mm`, lineHeight: 1.15, wordBreak: "break-word"}}>
                        {value}
                    </div>
                );
            case "price":
                return (
                    <div key={field.id} style={{fontWeight: 700, fontSize: `${base * 1.3}mm`, lineHeight: 1.1}}>
                        {Number(value).toFixed(2)} DH
                    </div>
                );
            case "barcode":
                return (
                    <div key={field.id} style={{display: "flex", justifyContent: "center", marginTop: "1mm"}}>
                        <BarcodeRenderer value={value} height={barcodeHeight} width={isSmall ? 1.1 : 1.5}/>
                    </div>
                );
            case "qr":
                return (
                    <div key={field.id} style={{display: "flex", justifyContent: "center", marginTop: "1mm"}}>
                        <QRCodeRenderer value={value} size={qrSize}/>
                    </div>
                );
            case "text":
            default:
                return (
                    <div key={field.id} style={{fontSize: `${base}mm`, lineHeight: 1.2, wordBreak: "break-word"}}>
                        {value}
                    </div>
                );
        }
    };

    return (
        <div
            style={{
                width: `${format.width}mm`,
                height: `${format.height}mm`,
                padding: "1.5mm",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: "0.6mm",
                overflow: "hidden",
                color: "#000",
                background: "#fff",
                fontFamily: "Arial, Helvetica, sans-serif",
            }}
        >
            {fields.map(renderField)}
        </div>
    );
};

// Mémoïsé : lors de l'impression d'un grand lot, les copies identiques d'une même
// étiquette (mêmes item/format/champs) ne sont pas re-rendues inutilement.
export default React.memo(LabelContent);
